import { FC, useEffect, useState } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useQuery } from 'react-query';

import { resolveQueueErrorMessage } from '../Initialize/resolveQueueErrorMessage';

import exerciseQueuesApi, { exerciseQueuesApiKeys } from 'api/rest/exerciseQueues/exerciseQueues';
import type { ExerciseQueueExplainRequestDto } from 'api/rest/exerciseQueues/exerciseQueues.types';
import LatexText from 'components/LatexText/LatexText';

import { ON_GO_COLORS } from '../onGoColors';
import useWritingAnimation from './useWritingAnimation';

const useStyles = makeStyles(theme => ({
  resultText: {
    color: theme.palette.text.primary,
  },
  explanationButton: {
    alignSelf: 'flex-start',
    textTransform: 'none',
    fontWeight: 600,
    marginTop: theme.spacing(0.5),
  },
  explanationButtonIncorrect: {
    color: ON_GO_COLORS.incorrect,
    borderColor: ON_GO_COLORS.incorrect,
    '&:hover': {
      borderColor: ON_GO_COLORS.incorrect,
      backgroundColor: ON_GO_COLORS.incorrectSoft,
    },
  },
  explanationText: {
    marginTop: theme.spacing(1),
    whiteSpace: 'pre-wrap',
    // KaTeX display math can be wider than the result card on small screens.
    '& .katex-display': {
      margin: theme.spacing(1, 0),
      overflowX: 'auto',
      overflowY: 'hidden',
    },
  },
  explanationCaret: {
    display: 'inline-block',
    width: 2,
    height: '1em',
    marginLeft: 1,
    verticalAlign: 'text-bottom',
    backgroundColor: 'currentColor',
    animation: '$blinkCaret 900ms step-end infinite',
  },
  '@keyframes blinkCaret': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
  explanationLoader: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  summaryRoot: {
    display: 'contents',
  },
  explanationContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    flexDirection: 'column',
  },
}));

type ExplanationButtonColor = 'primary' | 'incorrect';

type OnGoExerciseExplanationProps = {
  buttonColor: ExplanationButtonColor;
  onError: (message: string) => void;
  buttonClasses?: string;
  explanationClasses?: string;
  queueItemId: number;
};

const OnGoExerciseExplanation: FC<OnGoExerciseExplanationProps> = ({
  buttonColor,
  onError,
  queueItemId,
  buttonClasses,
  explanationClasses,
}) => {
  const classes = useStyles();
  const [isExplanationVisible, setIsExplanationVisible] = useState(false);
  const [explainParams, setExplainParams] = useState<ExerciseQueueExplainRequestDto | null>(null);

  const explainQuery = useQuery(
    explainParams ? exerciseQueuesApiKeys.explain(explainParams) : ['exerciseQueues', 'explain', 'idle'],
    () => {
      return exerciseQueuesApi.explain()(explainParams!);
    },
    {
      enabled: explainParams != null,
      refetchOnWindowFocus: false,
      onError: error => {
        setIsExplanationVisible(false);
        setExplainParams(null);
        onError(resolveQueueErrorMessage(error));
      },
    },
  );

  const explanationText = explainQuery.data?.text ?? null;
  const isExplanationLoading = explainQuery.isLoading || explainQuery.isFetching;
  const {
    displayedText: animatedExplanationText,
    isAnimating: isExplanationAnimating,
    reset: resetExplanationAnimation,
  } = useWritingAnimation(explanationText, { enabled: isExplanationVisible });

  useEffect(() => {
    setIsExplanationVisible(false);
    setExplainParams(null);
    resetExplanationAnimation();
  }, [queueItemId, resetExplanationAnimation]);

  const onToggleExplanation = () => {
    if (isExplanationVisible) {
      setIsExplanationVisible(false);
      return;
    }

    setIsExplanationVisible(true);

    if (explanationText != null || isExplanationLoading) {
      return;
    }

    setExplainParams({
      item_id: queueItemId,
    });
  };

  const button = (
    <Button
      className={clsx(classes.explanationButton, buttonColor === 'incorrect' && classes.explanationButtonIncorrect)}
      color={buttonColor === 'primary' ? 'primary' : undefined}
      disabled={isExplanationLoading}
      onClick={onToggleExplanation}
      size='small'
      variant={buttonColor === 'primary' ? 'contained' : 'outlined'}
    >
      {isExplanationVisible ? 'Ukryj wyjaśnienie' : 'Pokaż wyjaśnienie'}
    </Button>
  );

  const explanationContent = isExplanationVisible ? (
    isExplanationLoading && explanationText == null ? (
      <Box aria-busy='true' aria-label='Ładowanie wyjaśnienia' className={clsx(classes.explanationLoader, explanationClasses)}>
        <CircularProgress size={18} />
        <Typography className={classes.resultText} variant='body2'>
          Ładowanie wyjaśnienia…
        </Typography>
      </Box>
    ) : explanationText ? (
      <Typography
        aria-busy={isExplanationAnimating}
        aria-label='Wyjaśnienie'
        className={clsx(classes.resultText, classes.explanationText, explanationClasses)}
        variant='body2'
      >
        <LatexText hideIncompleteMath={isExplanationAnimating} text={animatedExplanationText} />
        {isExplanationAnimating ? <Box aria-hidden className={classes.explanationCaret} component='span' /> : null}
      </Typography>
    ) : null
  ) : null;

  return (
    <div className={classes.explanationContainer}>
      <div className={clsx(buttonClasses)}>{button}</div>
      {explanationContent}
    </div>
  );
};

export default OnGoExerciseExplanation;
